import { useGetSingePageSourceRule } from '@op/hooks/useGetPageSourceRule';
import {
  getLogValue,
  useRequestLatestEditLog,
} from '@op/pages/feeds/hooks/useFeedsRequest';
import { SentimentType } from '@op/types/common';
import moment from 'moment';
import { useMemo, useState } from 'react';

/**
 * 处理sentiment的分数
 * @param data 评论数据
 * @param realValue log数据
 * @param needRequest 是否需要请求接口获取log值
 * @param type 公共和新闻两个类型，两个类型的参数不一致
 * @returns
 */
export function useSentimentRating({
  data,
  realValue,
  needRequest = false,
  type = 'common',
}: {
  data: any;
  realValue?: SentimentType;
  needRequest?: boolean;
  type?: 'common' | 'news';
}) {
  const { Sentiment_Update } = useGetSingePageSourceRule({
    keys: 'Sentiment_Update',
  });

  const dataMemo = useMemo(() => {
    return data ? [data] : [];
  }, [data]);

  // 获取最后一条log的值
  const { logCommentMap } = useRequestLatestEditLog({
    data: dataMemo,
    needRequest,
    otherParams: type === 'news' ? { type: 1 } : {},
    type: ['sentiment'],
  });

  const [newSentiment, setNewSentiment] = useState<undefined | SentimentType>(
    undefined,
  );

  function changeNewSentiment(value: SentimentType) {
    setNewSentiment(value);
  }

  const [sentimentRatingValue, sentimentRealValue, filter] = useMemo((): [
    SentimentType,
    SentimentType,
    any,
  ] => {
    let {
      sentiment_rating,
      unified_id,
      edition_id,
      comment_time,
      comment_uin,
      create_time,
      edition_unified_id,
      md5_uni,
      release_time,
    } = data;
    let ratingValue = sentiment_rating ? parseFloat(sentiment_rating) : 0;
    let realSentimentValue: SentimentType = '1';
    if (ratingValue > 3.5) {
      realSentimentValue = '5';
    } else if (ratingValue > 2.5) {
      realSentimentValue = '3';
    }

    let defaultValue: SentimentType = realSentimentValue;

    // 如果有真值，那么就设置真值为默认值
    if (realValue) {
      defaultValue = String(realValue) as SentimentType;
    } else {
      const requestValue = getLogValue(
        logCommentMap,
        data,
        'latest_sentiment',
      ) as SentimentType;
      if (needRequest && requestValue) {
        defaultValue = requestValue;
      }
      // 如果有更新规则，并且没有真值
      else if (Sentiment_Update) {
        defaultValue = '0' as SentimentType;
      }
    }

    let commonFilter: any = {
      unified_id: unified_id,
      edition_id: edition_id,
      comment_time: moment(comment_time).format('YYYY-MM-DD HH:mm:ss'),
      comment_uin: comment_uin,
      comment_create_time: moment(create_time).format('YYYY-MM-DD HH:mm:ss'),
    };

    if (type === 'news') {
      commonFilter = {
        edition_unified_id: edition_unified_id,
        md5_uni: md5_uni,
        release_time: release_time,
      };
    }

    return [defaultValue, realSentimentValue, commonFilter];
  }, [data, realValue, logCommentMap]);

  return {
    sentimentRatingValue,
    filter,
    sentimentRealValue,
    Sentiment_Update,
    changeNewSentiment,
    newSentiment,
  };
}
